import React from 'react';
import { Link } from 'react-router-dom';
import { useMarket } from '../../context/MarketContext';
import { getProductPrice, getProductsForMarket } from '../../data/products';
import { productTheme } from '../../utils/productTheme';
import ProductIcon from './ProductIcon';

const ProductsSection: React.FC = () => {
  const { market } = useMarket();
  const marketProducts = getProductsForMarket(market);

  return (
    <section id="products" className="py-24 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1 rounded-full bg-primary-100 dark:bg-primary-500/10 text-primary-700 dark:text-primary-400 text-sm font-medium mb-4">
            Our Products
          </span>
          <h2 className="section-title">
            Products built to run{' '}
            <span className="gradient-text">real operations.</span>
          </h2>
          <p className="section-subtitle mx-auto mt-4">
            Powerful, cloud-based solutions designed to optimize your business operations and drive growth.
          </p>
        </div>

        <div className={`grid gap-8 ${marketProducts.length > 1 ? 'md:grid-cols-2 max-w-5xl' : 'max-w-2xl'} mx-auto`}>
          {marketProducts.map((product, index) => {
            const theme = productTheme(product.color);
            return (
              <Link
                key={product.id}
                to={`/products/${product.id}`}
                className="group card-glow relative overflow-hidden"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${theme.cardHoverWash} transition-all duration-500`} />

                <div className="relative">
                  <div className="flex items-start justify-between mb-6">
                    <div className={`w-16 h-16 rounded-2xl ${theme.iconTile} flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                      <ProductIcon icon={product.icon} className="w-8 h-8" />
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${theme.softBadge}`}>
                        {getProductPrice(product, market)}
                      </span>
                    </div>
                  </div>

                  <h3 className={`text-2xl font-display font-bold mb-3 transition-colors duration-300 text-slate-900 dark:text-white ${theme.textHover}`}>
                    {product.name}
                  </h3>
                  <p className="text-slate-600 dark:text-dark-300 mb-6">
                    {product.description}
                  </p>

                  <ul className="space-y-2 mb-6">
                    {product.features.slice(0, 5).map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-slate-700 dark:text-dark-400">
                        <svg className={`w-4 h-4 ${theme.textStrong}`} fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        {feature.title}
                      </li>
                    ))}
                  </ul>

                  <div className={`flex items-center gap-2 font-semibold ${theme.text} group-hover:gap-4 transition-all duration-300`}>
                    Learn more
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        <div className="text-center mt-12">
          <Link to="/products" className="btn-secondary inline-flex items-center gap-2">
            View All Products
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ProductsSection;
